import { ActionsQuery } from 'generated/resources/graphql'

import type { PopoverMenuTouchableOptionProps as Option } from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'
import users, { employeeUser } from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { useUnterminateEmployee } from 'modules/employees/hooks/useUnterminateEmployee'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import type Employee from 'modules/employees/store/models/Employee'

const invitedOptions = (
  employee: Employee,
  onClickUninvite: () => void,
  canInvite: boolean
): Option[] => {
  if (users.current === employeeUser(employee)) return [] // You should not be able to uninvite yourself

  if (!canInvite) return []

  return [
    {
      onClick: onClickUninvite,
      title: i18n.t('employees.uninvite.action'),
      description: i18n.t('employees.uninvite.action_description'),
    },
  ]
}

const unterminatedOptions = (
  employee: Employee,
  onClickTerminate: () => void,
  onClickBecome: () => void,
  onClickDisableMfa: () => void,
  onClickSetWelcomeSpaceAccess: () => void,
  canBecome: boolean,
  canDisableMfa: boolean | undefined,
  canTerminate: boolean,
  canInvite: boolean
): Option[] => {
  const name = employeeUser(employee).firstName

  const actions = []

  if (
    canInvite &&
    !employeeUser(employee).canLogin &&
    hasRegularAccess(employee.attributes.has_regular_access)
  ) {
    actions.push({
      onClick: onClickSetWelcomeSpaceAccess,
      title: i18n.t('employees.welcome_space.action'),
      description: i18n.t('employees.welcome_space.action_description'),
    })
  }

  if (canBecome) {
    actions.push({
      onClick: onClickBecome,
      title: i18n.t('employees.become.action', {
        name,
      }),
      description: i18n.t('employees.become.action_description', {
        name,
      }),
    })
  }

  if (canDisableMfa) {
    actions.push({
      onClick: onClickDisableMfa,
      title: i18n.t('employees.disable_mfa.action', {
        name,
      }),
      description: i18n.t('employees.disable_mfa.action_description'),
    })
  }

  if (canTerminate) {
    actions.push({
      onClick: onClickTerminate,
      title: i18n.t('employees.termination.action', {
        name,
      }),
      description: i18n.t('employees.termination.action_description'),
      danger: true,
    })
  }

  return actions
}

interface Props {
  employee: Employee
  queryData: ActionsQuery | undefined
  onClickUninvite: () => void
  onClickTerminate: () => void
  onClickBecome: () => void
  onClickDisableMfa: () => void | undefined
  onClickSetWelcomeSpaceAccess: () => void
  onClickActivate: () => void
}

const useOptions = ({
  employee,
  queryData,
  onClickUninvite,
  onClickTerminate,
  onClickBecome,
  onClickDisableMfa,
  onClickSetWelcomeSpaceAccess,
  onClickActivate,
}: Props): Option[] => {
  let options: Option[] = []
  const { unterminate } = useUnterminateEmployee()

  const terminatedOptions = (terminatedEmployee: Employee, onClick: () => void): Option[] => {
    const onClickUnterminate = async () => {
      if (terminatedEmployee.isTerminated) {
        onClick() // open ActivateModal to select type of access
      } else {
        await unterminateEmployee()
      }
    }

    if (terminatedEmployee.isEligibleForPayroll) return []

    const description = terminatedEmployee.isTerminated
      ? i18n.t('employees.untermination.action_description')
      : i18n.t('employees.stop_termination.action_description')

    return [
      {
        onClick: onClickUnterminate,
        title: i18n.t('employees.untermination.action'),
        description: description,
      },
    ]
  }

  async function unterminateEmployee() {
    const { data, error } = await unterminate({ id: employee.id })

    if (data?.employee?.id) {
      employee.set({ terminated_on: null })
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: error || i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const employeeTerminated = employee.attributes.terminated_on
  const canBecome = queryData?.employees.employees[0]?.access?._policies?.become.can || false
  const canDisableMfa =
    queryData?.employees.employees[0]?.authenticationAccess?._policies?.disableMfa.can || false
  const mfaEnabled = queryData?.employees.employees[0]?.authenticationAccess?.mfaEnabled || false
  const canTerminate = queryData?.employees.employees[0]?._policies?.terminateEmployees.can || false
  const canInvite = queryData?.employees.employees[0]?._policies?.inviteEmployeeToApp.can || false

  if (!employeeTerminated && employeeUser(employee).invited && !employeeUser(employee).joined) {
    options = [...options, ...invitedOptions(employee, onClickUninvite, canInvite)]
  }

  if (employeeTerminated) {
    options = [...options, ...terminatedOptions(employee, onClickActivate)]
  } else if (!employeeUser(employee).isCurrent()) {
    options = [
      ...options,
      ...unterminatedOptions(
        employee,
        onClickTerminate,
        onClickBecome,
        onClickDisableMfa,
        onClickSetWelcomeSpaceAccess,
        canBecome,
        canDisableMfa && mfaEnabled,
        canTerminate,
        canInvite
      ),
    ]
  }
  return options
}

export default useOptions
